import React, { useState, useMemo } from 'react';
import { MovaticFieldGroupAsyncSelect } from '../Redesing/movatic-field-group';
import useSearchDebounce from '../../hooks/search/useSearchDebounce';
import {
  useUsersRelayQuery,
  type UserGraphQL,
} from '../../hooks/users/query/V2/useUsersRelay';

export interface SelectedUser {
  /** User UUID. */
  id: string;
  /** User phone / legacy id. */
  oldId: string;
  /** Display label. */
  label: string;
  /** The full user record. */
  original: UserGraphQL;
}

interface UserOption {
  value: string;
  label: string;
  original: UserGraphQL;
}

/**
 * Whether a search term looks like a phone number (mostly digits).
 */
function looksLikePhone(term: string): boolean {
  const digits = term.replace(/[\s\-()+]/g, '');
  return digits.length > 0 && /^\d+$/.test(digits);
}

function userLabel(user: UserGraphQL): string {
  const name =
    user.fullName || [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  const contact = user.email || user.phone;
  if (name && contact) return `${name} (${contact})`;
  return name || contact || user.phone || user.id;
}

/**
 * Async-select for finding a user by name or phone, backed by the existing
 * `listUsers` Relay query. Emits the selected user to the parent.
 */
function UserSearch({
  systemId,
  organizationId,
  description,
  selectedUser,
  onChange,
}: {
  systemId?: string;
  organizationId?: string;
  description?: string;
  selectedUser?: { value: string; label: string } | null;
  onChange: (user: SelectedUser | null) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const debouncedSearch = useSearchDebounce(search, 500);

  // Uses the same `search` term the Users list pages use, which ORs the name match
  // with an exact phone match. This used to branch: a numeric-looking term set
  // only `phone`, so it skipped the name clause and a term like "484 667-3054"
  // returned nothing at all.
  const filter = useMemo(() => {
    const base: {
      systemId?: string;
      organizationId?: string;
      search?: string;
    } = {};
    if (systemId) base.systemId = systemId;
    if (organizationId) base.organizationId = organizationId;
    const term = debouncedSearch.trim();
    if (term) {
      base.search = looksLikePhone(term) ? term.replace(/\D/g, '') : term;
    }
    return base;
  }, [systemId, organizationId, debouncedSearch]);

  // Load as soon as a scope (system or organization) is available, so the
  // dropdown shows users on open — not only after typing. The search term
  // narrows the list as the user types.
  const { data, isLoading, isRefetching } = useUsersRelayQuery(filter, {
    limit: 25,
    enabled: Boolean(systemId) || Boolean(organizationId),
  });

  const options: UserOption[] = useMemo(
    () =>
      (data ?? []).map((user) => ({
        value: user.id,
        label: userLabel(user),
        original: user,
      })),
    [data]
  );

  return (
    <MovaticFieldGroupAsyncSelect
      isLoading={isLoading || isRefetching}
      label="User"
      description={description}
      name="user"
      id="coupon-assign-user"
      dataId="coupon-assign-user-select"
      placeholder="Search by name or phone"
      showClearButton
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      options={options}
      // The search runs on the server, so MUI must not filter again. Its
      // default compares the typed text against the option label — which is
      // "Name (email)" — so searching a phone found the user, then hid them
      // behind "No results".
      filterOptions={(opts: UserOption[]) => opts}
      value={selectedUser ?? null}
      onChange={(_event: React.SyntheticEvent, newValue: UserOption | null) => {
        if (newValue) {
          onChange({
            id: newValue.original.id,
            oldId: newValue.original.phone,
            label: newValue.label,
            original: newValue.original,
          });
        } else {
          onChange(null);
        }
        setIsOpen(false);
      }}
      getOptionText={(option: any) => {
        if (typeof option === 'string') return option;
        return option?.label ?? '';
      }}
      handleSearch={(e) => setSearch(e.target.value)}
      isOptionEqualToValue={(option: any, value: any) => {
        if (!option || !value) return false;
        return option.value === value.value;
      }}
    />
  );
}

export default UserSearch;
